import { Card, Grid } from '@material-ui/core';
import { AccountBalanceWallet, ArrowUpward, CheckCircle, EmailSharp, MobileFriendly, Person, Security, SwapHoriz } from '@material-ui/icons';
import React, { useEffect, useRef, useState } from "react";
import MemberAPI from '../../store/member/member.api';
import Loader from '../base/loader';

const initialStats = {
    totalMembers: 0,
    mobileVerifiedMembers: 0,
    emailVerifiedMembers: 0,
    twoFactorAuthmembers: 0,
    walletWhitelistedMembers: 0,
    tokenApprovedMembers: 0,
    inPlanMembers: 0,
    buyTransactionCount: 0,
    sellTransactionCount: 0,
    pendingPayoutCount: 0,
    pendingPayoutAmount: 0,
    pendingPayoutMembers: 0,
    pendingPayoutsByMethod: [],
};

const percentage = (value, total) => total ? Math.round((value / total) * 100) : 0;

const amount = (value) => {
    const parsed = parseFloat(value);
    if (!isFinite(parsed)) return "0.00";
    return parsed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });
};

const whole = (value) => Math.round(parseFloat(value) || 0).toLocaleString();

const prefersReducedMotion = () => typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Eases a number up to its target over `duration`, honouring reduced motion. */
function useCountUp(target, duration = 900) {
    const end = parseFloat(target) || 0;
    const [display, setDisplay] = useState(0);
    const from = useRef(0);

    useEffect(() => {
        if (prefersReducedMotion() || end === from.current) {
            setDisplay(end);
            from.current = end;
            return undefined;
        }

        const start = from.current;
        const startedAt = performance.now();
        let frame = requestAnimationFrame(function step(now) {
            const progress = Math.min(1, (now - startedAt) / duration);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(start + (end - start) * eased);
            if (progress < 1) {
                frame = requestAnimationFrame(step);
            } else {
                from.current = end;
            }
        });

        return () => cancelAnimationFrame(frame);
    }, [end, duration]);

    return display;
}

function AnimatedNumber({ value, format = whole, duration = 900, suffix = "" }) {
    const display = useCountUp(value, duration);
    return <>{format(display)}{suffix}</>;
}

export default function DashboardContainer() {
    const [isLoading, setLoading] = useState(false);
    const [stats, setStats] = useState(initialStats);
    // Bars start collapsed and grow once the data has landed.
    const [revealed, setRevealed] = useState(false);

    useEffect(() => {
        setLoading(true);
        MemberAPI.GetStats().then((d) => {
            setStats({ ...initialStats, ...d });
        }).finally(() => {
            setLoading(false);
            requestAnimationFrame(() => setRevealed(true));
        })
    }, [])

    const metrics = [
        { label: 'Total members', value: stats.totalMembers, detail: 'Registered accounts', icon: <Person />, tone: 'blue' },
        { label: 'Wallet readiness', value: percentage(stats.walletWhitelistedMembers, stats.totalMembers), suffix: '%', detail: `${stats.walletWhitelistedMembers} whitelisted`, icon: <AccountBalanceWallet />, tone: 'steel' },
        { label: 'Verified members', value: percentage(stats.emailVerifiedMembers, stats.totalMembers), suffix: '%', detail: `${stats.emailVerifiedMembers} email verified`, icon: <CheckCircle />, tone: 'amber' },
        { label: 'Transaction flow', value: (stats.buyTransactionCount || 0) + (stats.sellTransactionCount || 0), detail: `${stats.buyTransactionCount} buys / ${stats.sellTransactionCount} sells`, icon: <SwapHoriz />, tone: 'violet' },
    ];
    const readiness = [
        { label: 'Email verification', value: stats.emailVerifiedMembers, percent: percentage(stats.emailVerifiedMembers, stats.totalMembers), icon: <EmailSharp /> },
        { label: 'Mobile verification', value: stats.mobileVerifiedMembers, percent: percentage(stats.mobileVerifiedMembers, stats.totalMembers), icon: <MobileFriendly /> },
        { label: 'Two-factor security', value: stats.twoFactorAuthmembers, percent: percentage(stats.twoFactorAuthmembers, stats.totalMembers), icon: <Security /> },
    ];
    const totalTransactions = (stats.buyTransactionCount || 0) + (stats.sellTransactionCount || 0);

    return (
        <div className="dashboard-page">
            {isLoading && <Loader />}

            <section className="dashboard-intro dash-rise">
                <div>
                    <span className="dashboard-eyebrow">OPERATIONS OVERVIEW</span>
                    <h1>Good to see you.</h1>
                    <p>Monitor your network health and transaction activity from one place.</p>
                </div>
                <div className="dashboard-status"><span /> Systems operational</div>
            </section>

            <Grid container spacing={2} className="dashboard-metrics">
                {metrics.map((metric, index) => (
                    <Grid item xs={12} sm={6} lg={3} key={metric.label}>
                        <Card
                            elevation={0}
                            className={`metric-card metric-${metric.tone} dash-rise`}
                            style={{ animationDelay: `${index * 70}ms` }}
                        >
                            <div className="metric-topline">
                                <span className="metric-icon">{metric.icon}</span>
                                <ArrowUpward className="metric-trend" />
                            </div>
                            <strong>
                                <AnimatedNumber value={metric.value} suffix={metric.suffix || ""} />
                            </strong>
                            <span className="metric-label">{metric.label}</span>
                            <small>{metric.detail}</small>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2} className="dashboard-lower-grid">
                <Grid item xs={12} md={7}>
                    <Card elevation={0} className="dashboard-panel readiness-panel dash-rise" style={{ animationDelay: '280ms' }}>
                        <div className="panel-heading">
                            <div>
                                <span className="panel-kicker">NETWORK HEALTH</span>
                                <h2 className='mb-4'>Member readiness</h2>
                            </div>
                            <span className="panel-period">Live data</span>
                        </div>
                        <div className="readiness-list">
                            {readiness.map((item, index) => (
                                <div className="readiness-item" key={item.label}>
                                    <div className="readiness-icon">{item.icon}</div>
                                    <div className="readiness-main">
                                        <div className="readiness-copy">
                                            <span>{item.label}</span>
                                            <strong><AnimatedNumber value={item.percent} suffix="%" /></strong>
                                        </div>
                                        <div className="progress-track">
                                            <div
                                                style={{
                                                    width: `${revealed ? item.percent : 0}%`,
                                                    transitionDelay: `${index * 120}ms`,
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <small>{item.value} members</small>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Grid>
                <Grid item xs={12} md={5}>
                    <Card elevation={0} className="dashboard-panel activity-panel dash-rise" style={{ animationDelay: '350ms' }}>
                        <div className="panel-heading">
                            <div>
                                <span className="panel-kicker">TRANSACTION ACTIVITY</span>
                                <h2>Flow summary</h2>
                            </div>
                        </div>
                        <div className="flow-total">
                            <strong><AnimatedNumber value={totalTransactions} /></strong>
                            <span>total transactions</span>
                        </div>
                        <div className="flow-row">
                            <span className="flow-dot buy" />
                            <span>Buy transactions</span>
                            <strong><AnimatedNumber value={stats.buyTransactionCount} /></strong>
                        </div>
                        <div className="flow-row">
                            <span className="flow-dot sell" />
                            <span>Sell transactions</span>
                            <strong><AnimatedNumber value={stats.sellTransactionCount} /></strong>
                        </div>
                        <div className="activity-note">Review transaction queues regularly to keep settlement moving.</div>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} className="dashboard-lower-grid">
                <Grid item xs={12}>
                    <Card elevation={0} className="dashboard-panel payout-panel dash-rise" style={{ animationDelay: '420ms' }}>
                        <div className="panel-heading">
                            <div>
                                <span className="panel-kicker">PAYOUT QUEUE</span>
                                <h2>Awaiting settlement</h2>
                            </div>
                            <span className="panel-period">Status: Created</span>
                        </div>
                        <div className="payout-stats">
                            <div className="payout-stat">
                                <span>Payouts</span>
                                <strong><AnimatedNumber value={stats.pendingPayoutCount} /></strong>
                                <small>records created</small>
                            </div>
                            <div className="payout-stat payout-stat-amount">
                                <span>Total amount</span>
                                <strong>
                                    <AnimatedNumber value={stats.pendingPayoutAmount} format={amount} duration={1100} /> <em>USD</em>
                                </strong>
                                <small>yet to be paid out</small>
                            </div>
                            <div className="payout-stat">
                                <span>Members</span>
                                <strong><AnimatedNumber value={stats.pendingPayoutMembers} /></strong>
                                <small>unique beneficiaries</small>
                            </div>
                        </div>
                        {
                            (stats.pendingPayoutsByMethod || []).length > 0 &&
                            <div className="payout-breakdown">
                                {
                                    stats.pendingPayoutsByMethod.map((row, index) => (
                                        <div
                                            className="payout-row dash-fade"
                                            key={row.method}
                                            style={{ animationDelay: `${520 + index * 90}ms` }}
                                        >
                                            <span className={`flow-dot ${row.method === "Reward" ? "buy" : "sell"}`} />
                                            <span className="payout-row-label">{row.method}</span>
                                            <span className="payout-row-meta">{row.count} payouts · {row.members} members</span>
                                            <strong>{amount(row.amount)} USD</strong>
                                        </div>
                                    ))
                                }
                            </div>
                        }
                        {
                            stats.pendingPayoutCount === 0 &&
                            <div className="activity-note">Nothing is waiting in the payout queue right now.</div>
                        }
                    </Card>
                </Grid>
            </Grid>
        </div>
    );
}
